import React from 'react';
import { useGlobalState } from './GlobalStateProvider';
import { isMockMode } from '@utils/env';
import { selectClientPlan, selectDayTypeById, computeDayTypeTotals } from '@/selectors/nutritionClient';

export default function ClientNutritionPrint() {
  let clientId = '';
  let dayTypeId = '';
  // Querystring-based access: ?print=nutrition&clientId=...&dayTypeId=...
  try {
    const url = new URL(window.location.href);
    const isPrint = url.searchParams.get('print') === 'nutrition';
    const qClient = url.searchParams.get('clientId') || '';
    const qDay = url.searchParams.get('dayTypeId') || '';
    if (isPrint && qClient) clientId = qClient;
    if (isPrint && qDay) dayTypeId = qDay;
  } catch {
    /* ignored */
  }
  const state = useGlobalState();
  if (!clientId && isMockMode()) {
    clientId = 'c1';
  }
  let plan = selectClientPlan(state, clientId);
  // Fallback: if no clientId or plan not found, use first available plan
  if (!plan) {
    const plans = Array.isArray((state as any)?.clientNutritionPlans) ? (state as any).clientNutritionPlans : [];
    if (plans.length > 0) {
      plan = plans[0];
      if (!clientId) clientId = String((plan as any)?.clientId || '');
    }
  }
  let dt = selectDayTypeById(plan, dayTypeId);
  // Fallback: if no dayTypeId or not found, pick first day type
  if (!dt && plan && Array.isArray((plan as any)?.planData?.dayTypes) && (plan as any).planData.dayTypes.length > 0) {
    dt = (plan as any).planData.dayTypes[0];
    dayTypeId = String((dt as any)?.id || '');
  }
  const totals = computeDayTypeTotals(dt || undefined);

  if (!plan || !dt) {
    return <div style={{ padding: 24, fontFamily: 'system-ui' }}>Nothing to print.</div>;
  }

  const today = new Date();
  const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div
      style={{
        fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial',
        color: '#111',
        background: '#fff',
        padding: '32px 36px',
        maxWidth: 980,
        margin: '0 auto',
      }}
    >
      {/* Header */}
      <div
        className="no-print"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{plan.planData?.title || 'Nutrition Plan'}</div>
          <div style={{ fontSize: 13, opacity: 0.7 }}>{dt.name}</div>
        </div>
        <button
          onClick={() => window.print()}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff' }}
        >
          Print
        </button>
      </div>

      {/* Meta Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Client: <span style={{ color: '#111' }}>{String((plan as any)?.clientName || '—')}</span> · Date:{' '}
          <span style={{ color: '#111' }}>{dateStr}</span>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Generated by Coach'd</div>
      </div>

      {/* Macro Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Calories', value: `${Math.round(totals.calories)} kcal` },
          { label: 'Protein', value: `${totals.protein.toFixed(1)} g` },
          { label: 'Carbs', value: `${totals.carbs.toFixed(1)} g` },
          { label: 'Fat', value: `${totals.fat.toFixed(1)} g` },
        ].map((x) => (
          <div
            key={x.label}
            style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', background: '#fafafa' }}
          >
            <div style={{ fontSize: 12, color: '#6b7280' }}>{x.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{x.value}</div>
          </div>
        ))}
      </div>

      {/* Meals */}
      {(dt.meals || []).map((meal) => (
        <div
          key={meal.id}
          style={{ border: '1px solid #e5e7eb', borderRadius: 14, padding: '16px 18px', marginBottom: 14 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontWeight: 700 }}>{meal.name}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {Math.round(meal.totalNutrition?.calories ?? 0)} cal · P {(meal.totalNutrition?.protein ?? 0).toFixed(1)}{' '}
              · C {(meal.totalNutrition?.carbs ?? 0).toFixed(1)} · F {(meal.totalNutrition?.fat ?? 0).toFixed(1)}
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            {(meal.foods || []).map((f, i) => (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 8,
                  borderTop: i ? '1px solid #f3f4f6' : 'none',
                  padding: '8px 0',
                }}
              >
                <div>
                  <div style={{ fontSize: 14 }}>{f.foodItem?.name || 'Food'}</div>
                  <div
                    style={{ fontSize: 12, color: '#6b7280' }}
                  >{`${f.quantity} ${f.unit}${f.foodItem?.serving_size ? ` · ${f.foodItem.serving_size}` : ''}`}</div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 12 }}>
                  {Math.round(f.nutrition?.calories ?? 0)} cal
                  <div style={{ color: '#6b7280' }}>
                    P {(f.nutrition?.protein ?? 0).toFixed(1)} · C {(f.nutrition?.carbs ?? 0).toFixed(1)} · F{' '}
                    {(f.nutrition?.fat ?? 0).toFixed(1)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 16mm; }
        }
      `}</style>
    </div>
  );
}
